import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { safeFormatDate, formatDateTimeBerlin } from '../lib/dateTime';
import { formatHoursToHM } from '../lib/formatTime';

import { CheckSquare,
  Check,
  X,
  Clock,
  User,
  ChevronDown,
  ChevronUp,
  Briefcase,
  FolderKanban,
  Calendar,
  AlertTriangle,
  Building2,
  Banknote,
  ArrowDownRight,
  Edit2,
  History,
  Unlock,
  Lock,
  Sun,
  Gavel,
  Users,
  Shield,
  RotateCcw,
  Palmtree,
  Thermometer,
  Gift,
  Timer,
  Ban,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { GoBDDeleteConfirmDialog, useGoBDDelete } from '../components/GoBDDeleteConfirmDialog';
import { AuditHistoryPanel } from '../components/AuditHistoryPanel';
import { TeamAverageViolationsList } from '../components/AverageWorkTimeStatus';
import SundayRulesInfoBox, { SundayComplianceTeamList, FreeSundaysCounter } from '../components/SundayComplianceComponents';
import { DeputyActivationPanel, FixApprovalDialog, EscalationList } from '../components/DeputyAndFixingComponents';
import { MissingSubmissionsPanel } from '../components/MissingSubmissionsPanel';
import { TeamLeavesPanel } from '../components/TeamLeavesPanel';

const CHANGE_FIELD_LABELS = {
  date: 'Datum', start_time: 'Beginn', end_time: 'Ende', hours: 'Stunden', break_minutes: 'Pause (Min.)',
  note: 'Notiz', project_id: 'Projekt', category_id: 'Kategorie', description: 'Beschreibung', minutes: 'Minuten',
};
const CHANGE_TYPE_LABELS = { work_time: 'Arbeitszeit', project_time: 'Projektzeit', break_entries: 'Pause' };
const BASELINE_LABELS = { approved: 'seit der Genehmigung', rejected: 'seit der Ablehnung', withdrawn: 'seit der Zurückziehung', submitted: 'seit der Einreichung' };

const fmtHours = (h) => (h == null ? '–' : formatHoursToHM(Number(h)));
const fmtChangeValue = (field, values) => {
  if (!values) return '–';
  const v = values[field];
  if (field === 'hours') return fmtHours(v);
  if (field === 'project_id') return values.project_name || v || '–';
  if (field === 'category_id') return values.category_name || v || '–';
  return v == null || v === '' ? '–' : String(v);
};
const fmtChangeSummary = (collection, values) => {
  if (!values) return '';
  if (collection === 'project_time') return `${values.project_name || 'Projekt'}, ${fmtHours(values.hours)}`;
  if (collection === 'break_entries') return `${values.start_time || '?'}–${values.end_time || '?'}, ${values.minutes ?? '?'} Min.`;
  return `${values.start_time || '?'}–${values.end_time || '?'}, ${fmtHours(values.hours)}`;
};

function ApprovalChangesBox({ changes }) {
  if (!changes || !changes.count) return null;
  const baselineDate = changes.baseline_at ? new Date(changes.baseline_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' }) : '?';
  const byDate = changes.items.reduce((acc, item) => { (acc[item.date] = acc[item.date] || []).push(item); return acc; }, {});
  const kindMeta = {
    created: { label: 'Neu', cls: 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-300', Icon: Check },
    deleted: { label: 'Gelöscht', cls: 'bg-muted text-muted-foreground line-through', Icon: X },
    modified: { label: 'Geändert', cls: 'bg-amber-500/15 text-amber-800 dark:text-amber-300', Icon: Edit2 },
  };
  return (
    <div className="p-4 rounded-lg bg-indigo-500/10 border border-indigo-500/30" data-testid="approval-changes-box">
      <h4 className="text-sm font-medium text-indigo-800 dark:text-indigo-300 mb-3">
        Änderungen {BASELINE_LABELS[changes.baseline_type] || 'seit der Einreichung'} vom {baselineDate}{changes.baseline_by_name ? ` (${changes.baseline_by_name})` : ''}
      </h4>
      <div className="space-y-3">
        {Object.keys(byDate).sort().map((date) => (
          <div key={date}>
            <p className="text-xs font-medium text-muted-foreground mb-1">{safeFormatDate(date, 'dd.MM., EEEE')}</p>
            <ul className="space-y-2">
              {byDate[date].map((item) => {
                const meta = kindMeta[item.kind] || kindMeta.modified;
                const changedAt = item.last_changed_at ? new Date(item.last_changed_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' }) : '?';
                return (
                  <li key={item.document_id} className="text-sm text-foreground" data-testid={`change-item-${item.document_id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${meta.cls}`}><meta.Icon className="w-3 h-3" />{meta.label}</span>
                      <span className="text-muted-foreground">{CHANGE_TYPE_LABELS[item.collection_name] || item.collection_name}</span>
                      {item.kind === 'created' && <span className="font-medium">{fmtChangeSummary(item.collection_name, item.after)}</span>}
                      {item.kind === 'deleted' && <span className="line-through text-muted-foreground">{fmtChangeSummary(item.collection_name, item.before)}</span>}
                      {item.kind === 'modified' && item.changed_fields.map((f) => (
                        <span key={f} className="text-muted-foreground">
                          {CHANGE_FIELD_LABELS[f] || f}: <span className="line-through">{fmtChangeValue(f, item.before)}</span> → <span className="font-bold text-foreground">{fmtChangeValue(f, item.after)}</span>
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {item.last_changed_by || '?'}, {changedAt}{item.reasons?.length > 0 ? ` · Grund: ${item.reasons.join('; ')}` : ''}
                    </p>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}


export default function ApprovalsPage() {
  const { api, user } = useAuth();
  const [searchParams] = useSearchParams();
  const VALID_TABS = ['timesheets', 'overtime', 'unlock-requests', 'leaves', 'average-worktime', 'sunday-compliance'];
  const tabParam = searchParams.get('tab');
  const initialTab = tabParam === 'escalations'
    ? (user?.role === 'admin' ? 'escalations' : 'timesheets')
    : (VALID_TABS.includes(tabParam) ? tabParam : 'timesheets');
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [escalationCount, setEscalationCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [approvalDetails, setApprovalDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});
  
  // Overtime requests state
  const [pendingOvertimeRequests, setPendingOvertimeRequests] = useState([]);
  const [overtimeRejectDialogOpen, setOvertimeRejectDialogOpen] = useState(false);
  const [selectedOvertimeRequest, setSelectedOvertimeRequest] = useState(null);
  const [overtimeRejectReason, setOvertimeRejectReason] = useState('');
  
  // Reject dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  
  // Edit entry dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editApprovalId, setEditApprovalId] = useState(null);
  const [editEntry, setEditEntry] = useState(null);
  const [editForm, setEditForm] = useState({ start_time: '', end_time: '', break_minutes: 0, comment: '' });

  // GoBD Delete Dialog hook
  const { 
    dialogOpen: deleteDialogOpen, 
    setDialogOpen: setDeleteDialogOpen, 
    deleteConfig, 
    requestDelete, 
    handleConfirm: handleDeleteConfirm, 
    handleCancel: handleDeleteCancel 
  } = useGoBDDelete();
  
  // Audit History state
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  const [historyEntry, setHistoryEntry] = useState({ collection: '', id: '' });
  
  // Unlock requests state
  const [pendingUnlockRequests, setPendingUnlockRequests] = useState([]);
  const [unlockRejectDialogOpen, setUnlockRejectDialogOpen] = useState(false);
  const [selectedUnlockRequest, setSelectedUnlockRequest] = useState(null);
  const [unlockRejectReason, setUnlockRejectReason] = useState('');
  
  // Leave approval state
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [cancelPendingLeaves, setCancelPendingLeaves] = useState([]);
  const [overtimeBalances, setOvertimeBalances] = useState({});
  const [leaveRejectDialogOpen, setLeaveRejectDialogOpen] = useState(false);
  const [selectedLeaveRequest, setSelectedLeaveRequest] = useState(null);
  const [leaveRejectReason, setLeaveRejectReason] = useState('');
  const [cancelRejectDialogOpen, setCancelRejectDialogOpen] = useState(false);
  const [selectedCancelRequest, setSelectedCancelRequest] = useState(null);
  const [cancelRejectReason, setCancelRejectReason] = useState('');
  
  // Fix approval state
  const [fixDialogOpen, setFixDialogOpen] = useState(false);
  const [fixApprovalId, setFixApprovalId] = useState(null);
  
  const showHistory = (collection, id) => {
    setHistoryEntry({ collection, id });
    setHistoryPanelOpen(true);
  };

  const fetchApprovals = useCallback(async () => {
    try {
      const response = await api.get('/approvals/pending');
      setPendingApprovals(response.data);
    } catch (error) {
      console.error('Failed to fetch approvals:', error);
      toast.error('Fehler beim Laden der Genehmigungen');
    }
  }, [api]);

  const fetchOvertimeRequests = useCallback(async () => {
    try {
      const response = await api.get('/overtime/requests/pending');
      setPendingOvertimeRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch overtime requests:', error);
      // Don't show toast - user might not have permission
    }
  }, [api]);

  const fetchUnlockRequests = useCallback(async () => {
    try {
      const response = await api.get('/locks/unlock-requests/pending');
      setPendingUnlockRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch unlock requests:', error);
      // Don't show toast - user might not have permission
    }
  }, [api]);

  const fetchPendingLeaves = useCallback(async () => {
    try {
      const [leavesRes, cancelRes] = await Promise.all([
        api.get('/vacations/pending'),
        api.get('/vacations/cancellation-pending'),
      ]);
      setPendingLeaves(leavesRes.data);
      setCancelPendingLeaves(cancelRes.data);
    } catch (error) {
      console.error('Failed to fetch pending leaves:', error);
      // Don't show toast - user might not have permission
    }
  }, [api]);

  const fetchOvertimeBalances = useCallback(async () => {
    try {
      const response = await api.get('/overtime/team/balances');
      // Convert array to map: user_id -> balance info with committed data
      const balanceMap = {};
      response.data.forEach(item => {
        balanceMap[item.user_id] = {
          final_balance: item.final_balance,
          committed_total: item.committed_total || 0,
          committed_reduction_hours: item.committed_reduction_hours || 0,
          committed_payout_hours: item.committed_payout_hours || 0,
          available_after_commitments: item.available_after_commitments ?? item.final_balance,
        };
      });
      setOvertimeBalances(balanceMap);
    } catch (error) {
      console.error('Failed to fetch overtime balances:', error);
      // Don't show toast - user might not have permission
    }
  }, [api]);

  useEffect(() => {
    if (user?.role !== 'admin') return;
    api.get('/approvals/escalations')
      .then((res) => setEscalationCount(Array.isArray(res.data) ? res.data.length : 0))
      .catch(() => setEscalationCount(0));
  }, [api, user?.role]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([
        fetchApprovals(), 
        fetchOvertimeRequests(), 
        fetchUnlockRequests(),
        fetchPendingLeaves(),
        fetchOvertimeBalances(),
      ]);
      setIsLoading(false);
    };
    loadData();
  }, [fetchApprovals, fetchOvertimeRequests, fetchUnlockRequests, fetchPendingLeaves, fetchOvertimeBalances]);

  const fetchApprovalDetails = async (approvalId) => {
    if (approvalDetails[approvalId]) return; // Already loaded
    
    setLoadingDetails(prev => ({ ...prev, [approvalId]: true }));
    try {
      const response = await api.get(`/approvals/${approvalId}/details`);
      setApprovalDetails(prev => ({ ...prev, [approvalId]: response.data }));
    } catch (error) {
      console.error('Failed to fetch approval details:', error);
      toast.error('Fehler beim Laden der Details');
    } finally {
      setLoadingDetails(prev => ({ ...prev, [approvalId]: false }));
    }
  };

  const handleToggleExpand = async (approvalId) => {
    if (expandedId === approvalId) {
      setExpandedId(null);
    } else {
      setExpandedId(approvalId);
      await fetchApprovalDetails(approvalId);
    }
  };

  const handleApprove = async (approvalId) => {
    try {
      await api.post(`/approvals/${approvalId}/review`, { action: 'approve' });
      toast.success('Woche genehmigt');
      fetchApprovals();
      setExpandedId(null);
      // Notify Layout to update pending count
      window.dispatchEvent(new Event('ctime:approvals-changed'));
    } catch (error) {
      const status = error.response?.status;
      const message = error.response?.data?.detail || 'Fehler beim Genehmigen';
      if (status === 409) {
        toast.warning(message);
        fetchApprovals();
        window.dispatchEvent(new Event('ctime:approvals-changed'));
      } else {
        toast.error(message);
      }
    }
  };

  // Edit entry handlers
  const openEditDialog = (approvalId, workEntry) => {
    setEditApprovalId(approvalId);
    setEditEntry(workEntry);
    setEditForm({
      start_time: workEntry.start_time || '08:00',
      end_time: workEntry.end_time || '17:00',
      break_minutes: workEntry.break_minutes || 0,
      comment: '',
    });
    setEditDialogOpen(true);
  };

  const calculateEditHours = (start, end, breakMins) => {
    const [sH, sM] = start.split(':').map(Number);
    const [eH, eM] = end.split(':').map(Number);
    const totalMins = (eH * 60 + eM) - (sH * 60 + sM) - breakMins;
    return Math.max(0, Math.round((totalMins / 60) * 100) / 100);
  };

  const handleEditEntry = async () => {
    if (!editApprovalId || !editEntry) return;
    if (!editForm.comment.trim()) {
      toast.error('Bitte geben Sie einen Kommentar ein');
      return;
    }

    try {
      await api.post(`/approvals/${editApprovalId}/edit-entry`, {
        entry_id: editEntry.id,
        start_time: editForm.start_time,
        end_time: editForm.end_time,
        break_minutes: editForm.break_minutes,
        comment: editForm.comment,
      });
      toast.success('Zeiteintrag wurde angepasst');
      setEditDialogOpen(false);
      // Refresh details
      setApprovalDetails(prev => ({ ...prev, [editApprovalId]: null }));
      await fetchApprovalDetails(editApprovalId);
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Bearbeiten';
      toast.error(message);
    }
  };

  const openRejectDialog = (approval, day = null) => {
    setSelectedApproval(approval);
    setSelectedDay(day);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!selectedApproval) return;

    try {
      if (selectedDay) {
        // Reject specific day
        await api.post(`/approvals/${selectedApproval.id}/reject-day`, {
          date: selectedDay.date,
          reason: rejectReason,
        });
        toast.success(`Tag ${safeFormatDate(selectedDay.date, 'd. MMM')} abgelehnt`);
      } else {
        // Reject whole week
        await api.post(`/approvals/${selectedApproval.id}/review`, {
          action: 'reject',
          reason: rejectReason,
        });
        toast.success('Woche abgelehnt');
      }
      setRejectDialogOpen(false);
      fetchApprovals();
      setExpandedId(null);
      // Notify Layout to update pending count
      window.dispatchEvent(new Event('ctime:approvals-changed'));
    } catch (error) {
      const status = error.response?.status;
      const message = error.response?.data?.detail || 'Fehler beim Ablehnen';
      if (status === 409) {
        toast.warning(message);
        setRejectDialogOpen(false);
        fetchApprovals();
        window.dispatchEvent(new Event('ctime:approvals-changed'));
      } else {
        toast.error(message);
      }
    }
  };

  const getDayName = (dayName) => {
    const names = {
      Monday: 'Montag',
      Tuesday: 'Dienstag',
      Wednesday: 'Mittwoch',
      Thursday: 'Donnerstag',
      Friday: 'Freitag',
      Saturday: 'Samstag',
      Sunday: 'Sonntag',
    };
    return names[dayName] || dayName;
  };

  // Get absence style based on type
  const getAbsenceStyle = (absenceType) => {
    switch (absenceType) {
      case 'vacation':
        return { bg: 'bg-teal-500/20', border: 'border-teal-500/30', text: 'text-teal-400', icon: Palmtree };
      case 'sick':
        return { bg: 'bg-amber-500/20', border: 'border-amber-500/30', text: 'text-amber-400', icon: Thermometer };
      case 'special_leave':
        return { bg: 'bg-violet-500/20', border: 'border-violet-500/30', text: 'text-violet-400', icon: Gift };
      case 'overtime_reduction':
        return { bg: 'bg-sky-500/20', border: 'border-sky-500/30', text: 'text-sky-400', icon: Timer };
      default:
        return { bg: 'bg-muted', border: 'border-border/30', text: 'text-muted-foreground', icon: Calendar };
    }
  };

  // Get warning style based on type (for ArbZG compliance)
  const getWarningStyle = (warningType) => {
    if (warningType === 'max_hours' || warningType === 'rest_period') {
      return {
        text: 'text-red-600 dark:text-red-400',
        bg: 'bg-red-500/10',
        border: 'border-red-500/30',
        severity: 2
      };
    }
    // break and review warnings are amber
    return {
      text: 'text-amber-600 dark:text-amber-400',
      bg: 'bg-amber-500/10',
      border: 'border-amber-500/30',
      severity: 1
    };
  };

  // Get day border class based on compliance warnings
  const getDayComplianceBorderClass = (day) => {
    const warnings = day.compliance?.warnings || [];
    if (warnings.length === 0) return '';
    
    // Check for red warnings (max_hours or rest_period)
    const hasRedWarning = warnings.some(w => w.type === 'max_hours' || w.type === 'rest_period');
    if (hasRedWarning) return 'border-red-500/50';
    
    // Otherwise amber (break or review)
    return 'border-amber-500/50';
  };

  // Build compliance summary tooltip
  const getComplianceTooltip = (summary) => {
    if (!summary || summary.total === 0) return '';
    const parts = [];
    if (summary.max_hours > 0) parts.push(`Höchstarbeitszeit: ${summary.max_hours}`);
    if (summary.break > 0) parts.push(`Pausen: ${summary.break}`);
    if (summary.rest_period > 0) parts.push(`Ruhezeit: ${summary.rest_period}`);
    if (summary.review > 0) parts.push(`Endzeit fehlt: ${summary.review}`);
    return parts.join(' · ');
  };

  // Check if compliance summary has red-severity warnings
  const hasRedComplianceWarnings = (summary) => {
    return summary && (summary.max_hours > 0 || summary.rest_period > 0);
  };

  // Overtime request handlers
  const handleApproveOvertimeRequest = async (requestId) => {
    try {
      await api.post(`/overtime/requests/${requestId}/approve`);
      toast.success('Antrag zum Arbeitszeitkonto genehmigt');
      fetchOvertimeRequests();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Genehmigen';
      toast.error(message);
    }
  };

  const openOvertimeRejectDialog = (request) => {
    setSelectedOvertimeRequest(request);
    setOvertimeRejectReason('');
    setOvertimeRejectDialogOpen(true);
  };

  const handleRejectOvertimeRequest = async () => {
    if (!selectedOvertimeRequest) return;

    try {
      await api.post(`/overtime/requests/${selectedOvertimeRequest.id}/reject?reason=${encodeURIComponent(overtimeRejectReason || '')}`);
      toast.success('Antrag zum Arbeitszeitkonto abgelehnt');
      setOvertimeRejectDialogOpen(false);
      fetchOvertimeRequests();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Ablehnen';
      toast.error(message);
    }
  };

  const getOvertimeRequestTypeLabel = (type) => {
    return type === 'reduction' ? 'Zeitausgleich' : 'Auszahlung Zeitguthaben';
  };

  // Unlock request handlers
  const handleApproveUnlockRequest = async (requestId) => {
    try {
      await api.post(`/locks/unlock-requests/${requestId}/approve`, {});
      toast.success('Entsperrungsantrag genehmigt');
      fetchUnlockRequests();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Genehmigen';
      toast.error(message);
    }
  };

  const openUnlockRejectDialog = (request) => {
    setSelectedUnlockRequest(request);
    setUnlockRejectReason('');
    setUnlockRejectDialogOpen(true);
  };

  const handleRejectUnlockRequest = async () => {
    if (!selectedUnlockRequest) return;
    if (!unlockRejectReason.trim()) {
      toast.error('Bitte geben Sie einen Ablehnungsgrund ein');
      return;
    }

    try {
      await api.post(`/locks/unlock-requests/${selectedUnlockRequest.id}/reject`, {
        comment: unlockRejectReason.trim(),
      });
      toast.success('Entsperrungsantrag abgelehnt');
      setUnlockRejectDialogOpen(false);
      fetchUnlockRequests();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Ablehnen';
      toast.error(message);
    }
  };

  const getCollectionLabel = (collection) => {
    const labels = {
      'work_time': 'Arbeitszeit',
      'project_time': 'Projektzeit',
      'mileage_entries': 'Fahrtkosten',
      'vacations': 'Urlaub',
    };
    return labels[collection] || collection;
  };

  // Leave type helpers
  const getLeaveTypeLabel = (type) => {
    const labels = { 
      vacation: 'Urlaub', 
      sick: 'Krankmeldung', 
      special_leave: 'Sonderurlaub',
      special: 'Sonderurlaub',
      overtime_reduction: 'Zeitausgleich'
    };
    return labels[type] || 'Abwesenheit';
  };

  const getLeaveTypeIcon = (type) => {
    switch (type) {
      case 'sick': return Thermometer;
      case 'special_leave':
      case 'special': return Gift;
      case 'overtime_reduction': return Timer;
      default: return Palmtree;
    }
  };

  const getLeaveTypeStyle = (type) => {
    switch (type) {
      case 'vacation':
        return { bg: 'bg-teal-500/20', border: 'border-teal-500/30', text: 'text-teal-400' };
      case 'sick':
        return { bg: 'bg-amber-500/20', border: 'border-amber-500/30', text: 'text-amber-400' };
      case 'special_leave':
      case 'special':
        return { bg: 'bg-violet-500/20', border: 'border-violet-500/30', text: 'text-violet-400' };
      case 'overtime_reduction':
        return { bg: 'bg-blue-500/20', border: 'border-blue-500/30', text: 'text-blue-400' };
      default:
        return { bg: 'bg-muted', border: 'border-border/30', text: 'text-muted-foreground' };
    }
  };

  // Leave approval handlers
  const handleApproveLeave = async (leaveId) => {
    try {
      await api.post(`/vacations/${leaveId}/review`, { action: 'approve' });
      toast.success('Antrag genehmigt');
      fetchPendingLeaves();
      fetchOvertimeBalances();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Genehmigen';
      toast.error(message);
    }
  };

  const openLeaveRejectDialog = (leave) => {
    setSelectedLeaveRequest(leave);
    setLeaveRejectReason('');
    setLeaveRejectDialogOpen(true);
  };

  const handleRejectLeave = async () => {
    if (!selectedLeaveRequest) return;
    try {
      await api.post(`/vacations/${selectedLeaveRequest.id}/review`, {
        action: 'reject',
        reason: leaveRejectReason,
      });
      toast.success('Antrag abgelehnt');
      setLeaveRejectDialogOpen(false);
      fetchPendingLeaves();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Ablehnen';
      toast.error(message);
    }
  };

  const handleConfirmCancellation = async (leaveId) => {
    try {
      await api.post(`/vacations/${leaveId}/cancel-respond`, { action: 'confirm' });
      toast.success('Stornierung bestätigt');
      fetchPendingLeaves();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler';
      toast.error(message);
    }
  };

  const openCancelRejectDialog = (leave) => {
    setSelectedCancelRequest(leave);
    setCancelRejectReason('');
    setCancelRejectDialogOpen(true);
  };

  const handleRejectCancellation = async () => {
    if (!selectedCancelRequest) return;
    try {
      await api.post(`/vacations/${selectedCancelRequest.id}/cancel-respond`, {
        action: 'reject',
        reason: cancelRejectReason || undefined,
      });
      toast.success('Stornierung abgelehnt');
      setCancelRejectDialogOpen(false);
      fetchPendingLeaves();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler';
      toast.error(message);
    }
  };
  const handleWithdrawCancellation = async (leaveId) => {
    try {
      await api.post(`/vacations/${leaveId}/cancel-withdraw`);
      toast.success('Stornierungsvorschlag zurückgezogen');
      fetchPendingLeaves();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    }
  };  

  // Helper to calculate overtime reduction hours
  const calculateOvertimeReductionHours = (leave) => {
    // Calculate working days in the leave period
    const startDate = new Date(leave.start_date);
    const endDate = new Date(leave.end_date);
    let workDays = 0;
    
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dayOfWeek = d.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Not weekend
        workDays++;
      }
    }
    
    // Use daily_target_hours from leave if available, otherwise use 8h default
    const dailyHours = leave.daily_target_hours || 8;
    const isEstimate = !leave.daily_target_hours;
    const totalHours = workDays * dailyHours;
    
    return { workDays, dailyHours, totalHours, isEstimate };
  };

  // Eigene Stornierungsvorschläge an andere sind keine offene Aufgabe
  const respondCancelCount = cancelPendingLeaves.filter((l) => l.can_respond !== false).length;
  const totalPendingCount = pendingApprovals.length + pendingOvertimeRequests.length + pendingUnlockRequests.length + pendingLeaves.length + respondCancelCount;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="approvals-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">Genehmigungen</h1>
        <p className="text-muted-foreground mt-1">
          {totalPendingCount} {totalPendingCount === 1 ? 'ausstehende Genehmigung' : 'ausstehende Genehmigungen'}
        </p>
      </div>

      {/* Tabs for different approval types */}
      <Tabs defaultValue={initialTab} className="w-full">
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="timesheets" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
            Wochen-Genehmigungen
            {pendingApprovals.length > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-400">
                {pendingApprovals.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="overtime" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
            Anträge zum Arbeitszeitkonto
            {pendingOvertimeRequests.length > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-400">
                {pendingOvertimeRequests.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="unlock-requests" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400">
            <Unlock className="w-4 h-4 mr-1" />
            Entsperrungsanträge
            {pendingUnlockRequests.length > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-red-500/20 text-red-400">
                {pendingUnlockRequests.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="leaves" className="data-[state=active]:bg-teal-500/20 data-[state=active]:text-teal-400" data-testid="leaves-approval-tab">
            <Palmtree className="w-4 h-4 mr-1" />
            Abwesenheiten
            {(pendingLeaves.length + respondCancelCount) > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-400">
                {pendingLeaves.length + respondCancelCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="average-worktime" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400">
            <Clock className="w-4 h-4 mr-1" />
            Ø Arbeitszeit
          </TabsTrigger>
          <TabsTrigger value="sunday-compliance" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400">
            <Sun className="w-4 h-4 mr-1" />
            Sonntagsarbeit
          </TabsTrigger>
          {user?.role === 'admin' && (
            <TabsTrigger value="escalations" className="data-[state=active]:bg-red-500/20 data-[state=active]:text-red-400" data-testid="escalations-tab">
              <Gavel className="w-4 h-4 mr-1" />
              Eskalationen
              {escalationCount > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-500 text-white">{escalationCount}</span>
              )}
            </TabsTrigger>
          )}
          {(user?.role === 'supervisor' || user?.role === 'admin') && (
            <TabsTrigger value="deputy" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400" data-testid="deputy-tab">
              <Users className="w-4 h-4 mr-1" />
              Vertretung
            </TabsTrigger>
          )}
        </TabsList>

        {/* Timesheet Approvals Tab */}
        <TabsContent value="timesheets" className="mt-6">
          {/* Missing Submissions Panel - only for supervisor/admin */}
          {(user?.role === 'supervisor' || user?.role === 'admin') && (
            <MissingSubmissionsPanel />
          )}
          
          <div className="space-y-4">
        {pendingApprovals.map((approval, index) => {
          const details = approvalDetails[approval.id];
          const isExpanded = expandedId === approval.id;
          const isLoadingDetail = loadingDetails[approval.id];

          return (
            <motion.div
              key={approval.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="card-glass overflow-hidden"
            >
              {/* Header */}
              <div
                className="p-4 md:p-6 cursor-pointer hover:bg-accent/20 transition-colors"
                onClick={() => handleToggleExpand(approval.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center">
                      <User className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-medium text-foreground">
                          {approval.user?.first_name} {approval.user?.last_name}
                        </h3>
                        {/* Compliance Summary Badge */}
                        {approval.compliance_summary?.total > 0 && (
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                              hasRedComplianceWarnings(approval.compliance_summary)
                                ? 'bg-red-500/20 text-red-600 dark:text-red-400'
                                : 'bg-amber-500/20 text-amber-600 dark:text-amber-400'
                            }`}
                            title={getComplianceTooltip(approval.compliance_summary)}
                          >
                            <AlertTriangle className="w-3 h-3" />
                            {approval.compliance_summary.total} {approval.compliance_summary.total === 1 ? 'Hinweis' : 'Hinweise'}
                          </span>
                        )}
                        {/* Reopen Badge */}
                        {approval.reopen_reason && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-indigo-500/15 text-indigo-800 dark:text-indigo-300"
                            title={`Zuvor genehmigt am ${approval.previously_approved_at ? new Date(approval.previously_approved_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' }) : '?'} von ${approval.previously_approved_by_name || '?'}`}
                          >
                            <RotateCcw className="w-3 h-3" />
                            Erneut eingereicht
                          </span>
                        )}
                        {/* Objection Badge (fixed weeks with escalation) */}
                        {approval.status === 'fixed_by_supervisor' && approval.escalated_to_management && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-500/15 text-amber-800 dark:text-amber-300"
                            title={approval.objection_reason ? `Einspruch: ${approval.objection_reason}` : 'Einspruch eingelegt'}
                            data-testid={`objection-badge-${approval.id}`}
                          >
                            <Gavel className="w-3 h-3" />
                            Einspruch eingelegt
                          </span>
                        )}
                        {/* Changes since baseline Badge */}
                        {approval.change_count > 0 && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-indigo-500/15 text-indigo-800 dark:text-indigo-300"
                            title="seit Genehmigung/Ablehnung"
                            data-testid={`change-count-badge-${approval.id}`}
                          >
                            {approval.change_count} {approval.change_count === 1 ? 'Änderung' : 'Änderungen'}
                          </span>
                        )}
                        {/* Post-submit changes Badge (only if not reopened and no change_count) */}
                        {!approval.reopen_reason && approval.post_submit_change_count > 0 && !(approval.change_count > 0) && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-indigo-500/15 text-indigo-800 dark:text-indigo-300"
                          >
                            Nach Einreichung geändert ({approval.post_submit_change_count})
                          </span>
                        )}
                        {/* Submission number badges */}
                        {approval.submission_number > 1 && approval.previous_status === 'rejected' && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-500/15 text-amber-800 dark:text-amber-300"
                            title={`Zuvor abgelehnt am ${approval.previous_reviewed_at ? new Date(approval.previous_reviewed_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' }) : '?'}: ${approval.previous_rejection_reason || 'Kein Grund angegeben'}`}
                          >
                            {approval.submission_number}. Einreichung
                          </span>
                        )}
                        {approval.submission_number > 1 && approval.previous_status === 'withdrawn' && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground"
                            title="Zuvor zurückgezogen"
                          >
                            {approval.submission_number}. Einreichung
                          </span>
                        )}
                        {approval.submission_number > 1 && approval.previous_status === 'unlocked' && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-slate-500/15 text-slate-800 dark:text-slate-300"
                            title={`Nach Entsperrung erneut eingereicht: ${approval.previous_rejection_reason || 'Kein Grund angegeben'}`}
                          >
                            {approval.submission_number}. Einreichung
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        KW {safeFormatDate(approval.week_start, 'w')} • {safeFormatDate(approval.week_start, 'd. MMM')} - {safeFormatDate(approval.week_end, 'd. MMM yyyy')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    {/* Summary stats */}
                    <div className="hidden md:flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Arbeitszeit</p>
                        <p className="text-lg font-mono font-bold text-foreground">{formatHoursToHM(approval.work_hours)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Projektzeit</p>
                        <p className="text-lg font-mono font-bold text-lime-600 dark:text-lime-400">{formatHoursToHM(approval.project_hours)}</p>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApprove(approval.id);
                        }}
                        data-testid={`approve-all-${approval.id}`}
                        className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Alles
                      </Button>
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openRejectDialog(approval);
                        }}
                        data-testid={`reject-all-${approval.id}`}
                        className="bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Ablehnen
                      </Button>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Mobile stats */}
                <div className="mt-4 md:hidden flex items-center gap-6">
                  <div>
                    <p className="text-xs text-muted-foreground">Arbeitszeit</p>
                    <p className="text-lg font-mono font-bold text-foreground">{formatHoursToHM(approval.work_hours)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Projektzeit</p>
                    <p className="text-lg font-mono font-bold text-lime-600 dark:text-lime-400">{formatHoursToHM(approval.project_hours)}</p>
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="border-t border-border"
                  >
                    {isLoadingDetail ? (
                      <div className="p-8 flex items-center justify-center">
                        <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : details ? (
                      <div className="p-4 md:p-6 space-y-4">
                        {/* Objection hint for fixed weeks */}
                        {details.status === 'fixed_by_supervisor' && details.escalated_to_management && (
                          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 flex flex-wrap items-center gap-2" data-testid="details-objection-hint">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-500/15 text-amber-800 dark:text-amber-300">
                              <Gavel className="w-3 h-3" />
                              Einspruch eingelegt
                            </span>
                            {details.objection_reason && (
                              <span className="text-sm text-amber-800 dark:text-amber-300">{details.objection_reason}</span>
                            )}
                          </div>
                        )}

                        {/* Changes since baseline */}
                        <ApprovalChangesBox changes={details.changes} />

                        {/* Post-Submit Changes Box (only if no baseline changes) */}
                        {!(details.changes?.count > 0) && details.post_submit_changes && details.post_submit_changes.length > 0 && (
                          <div className="p-4 rounded-lg bg-indigo-500/10 border border-indigo-500/30">
                            <h4 className="text-sm font-medium text-indigo-800 dark:text-indigo-300 mb-2">Änderungen nach Einreichung</h4>
                            <ul className="space-y-1 text-sm text-indigo-800 dark:text-indigo-300">
                              {details.post_submit_changes.slice(0, 10).map((change, idx) => {
                                const actionText = change.action === 'create' ? 'angelegt' : change.action === 'update' ? 'geändert' : 'gelöscht';
                                const typeText = change.collection_name === 'work_time' ? 'Arbeitszeit' : change.collection_name === 'project_time' ? 'Projektzeit' : 'Pause';
                                const dateTime = change.at ? new Date(change.at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' }) : '?';
                                return (
                                  <li key={idx}>{dateTime} – {typeText} {actionText}</li>
                                );
                              })}
                            </ul>
                            {details.post_submit_changes.length > 10 && (
                              <p className="text-sm text-indigo-800 dark:text-indigo-300 mt-2">und {details.post_submit_changes.length - 10} weitere</p>
                            )}
                          </div>
                        )}

                        {/* Submission History Box */}
                        {details.history && details.history.length > 0 && (
                          <div className="p-4 rounded-lg bg-muted/50 border border-border">
                            <h4 className="text-sm font-medium text-foreground mb-2">Einreichungsverlauf</h4>
                            <ul className="space-y-2 text-sm">
                              {details.history.map((entry, idx) => {
                                const statusBadge = entry.status === 'rejected' 
                                  ? <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-amber-500/15 text-amber-800 dark:text-amber-300">Abgelehnt</span>
                                  : entry.status === 'withdrawn'
                                  ? <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground">Zurückgezogen</span>
                                  : entry.status === 'approved'
                                  ? <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-500/15 text-emerald-800 dark:text-emerald-300">Genehmigt</span>
                                  : entry.status === 'unlocked'
                                  ? <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-slate-500/15 text-slate-800 dark:text-slate-300">Entsperrt</span>
                                  : <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-500/15 text-indigo-800 dark:text-indigo-300">Offen</span>;
                                const submittedDate = entry.submitted_at ? new Date(entry.submitted_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' }) : '?';
                                return (
                                  <li key={idx} className="flex flex-wrap items-center gap-2 text-muted-foreground">
                                    {statusBadge}
                                    <span>eingereicht am {submittedDate}</span>
                                    {entry.status === 'rejected' && entry.reviewed_at && (
                                      <span>von {entry.reviewer_name || '?'} am {new Date(entry.reviewed_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' })}: {entry.rejection_reason || 'Kein Grund'}</span>
                                    )}
                                    {entry.status === 'withdrawn' && entry.withdrawn_at && (
                                      <span>zurückgezogen am {new Date(entry.withdrawn_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' })}</span>
                                    )}
                                    {entry.status === 'unlocked' && entry.unlocked_at && (
                                      <span>am {new Date(entry.unlocked_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' })} von {entry.unlocked_by_name || 'System'}: {entry.unlock_reason || 'Kein Grund'}</span>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}

                        {/* Reopen Info */}
                        {details.reopen_reason && (
                          <div className="p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/30">
                            <p className="text-sm text-indigo-800 dark:text-indigo-300">
                              Zuvor genehmigt am {details.previously_approved_at ? new Date(details.previously_approved_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' }) : '?'} von {details.previously_approved_by_name || '?'}; {details.reopen_reason === 'entry_added_after_approval' ? 'nachträgliche Buchung' : 'nach Entsperrung'}
                            </p>
                          </div>
                        )}

                        {/* Days Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
                          {details.days.map((day) => {
                            const complianceBorderClass = getDayComplianceBorderClass(day);
                            const dayChanged = details.changes?.items?.some((c) => c.date === day.date);
                            return (
                            <div
                              key={day.date}
                              className={`p-3 rounded-lg border ${
                                day.is_weekend
                                  ? 'bg-card/50 border-border opacity-60'
                                  : day.holiday
                                  ? 'bg-amber-500/5 border-amber-500/20'
                                  : complianceBorderClass
                                  ? `bg-accent/20 ${complianceBorderClass}`
                                  : 'bg-accent/20 border-border'
                              }`}
                            >
                              {/* Day Header */}
                              <div className="flex items-center justify-between mb-2">
                                <div>
                                  <p className="text-xs text-muted-foreground">{getDayName(day.day_name)}</p>
                                  <p className="text-sm font-medium text-foreground">
                                    {safeFormatDate(day.date, 'd. MMM')}
                                  </p>
                                  {dayChanged && (
                                    <span className="inline-flex items-center gap-1 text-[10px] text-indigo-700 dark:text-indigo-300" data-testid={`day-changed-${day.date}`}>
                                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />geändert
                                    </span>
                                  )}
                                </div>
                                {!day.is_weekend && !day.holiday && day.work_hours > 0 && (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openRejectDialog(approval, day)}
                                    className="h-7 px-2 text-muted-foreground hover:text-red-400 hover:bg-red-500/10"
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>

                              {/* Holiday Badge */}
                              {day.holiday && (
                                <div className="mb-2 px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded">
                                  {day.holiday}
                                </div>
                              )}

                              {/* Absence Badge - shown when not holiday (holiday takes precedence) */}
                              {!day.holiday && day.absence && (() => {
                                const style = getAbsenceStyle(day.absence.type);
                                const AbsenceIcon = style.icon;
                                return (
                                  <div
                                    className={`mb-2 px-2 py-1.5 ${style.bg} ${style.text} text-xs rounded border ${style.border} ${day.absence.confirmed === false ? 'border-dashed' : ''} flex flex-wrap items-center gap-1.5`}
                                    data-testid={`day-absence-${day.date}`}
                                  >
                                    <AbsenceIcon className="w-3.5 h-3.5" />
                                    <span className="font-medium">{day.absence.label}</span>
                                    {day.absence.status_label && (
                                      <span className="opacity-80">({day.absence.status_label})</span>
                                    )}
                                  </div>
                                );
                              })()}

                              {/* Work Time - supports multiple entries */}
                              {(day.work_entries || (day.work_time ? [day.work_time] : [])).length > 0 ? (
                                <div className="mb-2 space-y-1">
                                  <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                                    <Briefcase className="w-3 h-3" />
                                    Arbeitszeit
                                  </div>
                                  {(day.work_entries || [day.work_time]).map((we) => (
                                    <div key={we.id} className={`p-2 rounded ${we.admin_edit ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-indigo-500/10'}`}>
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-muted-foreground">
                                          {we.start_time} - {we.end_time}
                                        </span>
                                        <div className="flex items-center gap-1">
                                          <span className="text-sm font-mono font-bold text-foreground">
                                            {formatHoursToHM(Number(we.hours))}
                                          </span>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); openEditDialog(approval.id, we); }}
                                            data-testid={`edit-entry-${we.id}`}
                                            className="p-0.5 text-muted-foreground hover:text-indigo-600 dark:text-indigo-400"
                                            title="Bearbeiten"
                                          >
                                            <Edit2 className="w-3 h-3" />
                                          </button>
                                        </div>
                                      </div>
                                      {we.break_minutes > 0 && (
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                          Pause: {we.break_minutes} Min
                                        </p>
                                      )}
                                      {we.admin_edit && (
                                        <div className="mt-1 pt-1 border-t border-amber-500/20">
                                          <p className="text-[10px] text-amber-600 dark:text-amber-400">
                                            Korrigiert: {we.admin_edit.original_values?.start_time}-{we.admin_edit.original_values?.end_time} ({formatHoursToHM(Number(we.admin_edit.original_values?.hours))})
                                          </p>
                                          <p className="text-[10px] text-muted-foreground italic mt-0.5">
                                            &ldquo;{we.admin_edit.comment}&rdquo;
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                  {(day.work_entries || []).length > 1 && (
                                    <div className="flex justify-end pt-1">
                                      <span className="text-xs font-mono text-foreground">Σ {formatHoursToHM(day.work_hours)}</span>
                                    </div>
                                  )}
                                </div>
                              ) : !day.is_weekend && !day.holiday && !day.absence ? (
                                <div className="mb-2 p-2 rounded bg-muted/50">
                                  <p className="text-xs text-muted-foreground">Keine Arbeitszeit</p>
                                </div>
                              ) : null}

                              {/* ArbZG Compliance Warnings */}
                              {day.compliance?.warnings?.length > 0 && (
                                <div className="mb-2 space-y-1">
                                  {day.compliance.warnings.map((warning, idx) => {
                                    const style = getWarningStyle(warning.type);
                                    return (
                                      <div
                                        key={idx}
                                        className={`flex items-start gap-1.5 px-2 py-1 rounded text-xs ${style.bg} ${style.text}`}
                                      >
                                        <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                        <span>{warning.message}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Rest period not checkable hint */}
                              {day.compliance?.rest_not_checkable && !day.compliance?.warnings?.length && (
                                <div className="mb-2 px-2 py-1 rounded text-xs bg-muted/50 text-muted-foreground">
                                  Ruhezeit nicht prüfbar
                                </div>
                              )}

                              {/* Project Time */}
                              {day.project_entries.length > 0 ? (
                                <div className="space-y-1">
                                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                    <FolderKanban className="w-3 h-3" />
                                    Projektzeit
                                  </div>
                                  {day.project_entries.map((entry, idx) => (
                                    <div
                                      key={idx}
                                      className="p-2 rounded bg-lime-500/10 border border-lime-500/10"
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                          <p className="text-xs font-medium text-foreground truncate">
                                            {entry.project_name}
                                          </p>
                                          <p className="text-xs text-muted-foreground truncate">
                                            {entry.customer_name}
                                          </p>
                                        </div>
                                        <span className="text-sm font-mono font-bold text-lime-600 dark:text-lime-400 ml-2">
                                          {formatHoursToHM(Number(entry.hours))}
                                        </span>
                                      </div>
                                      {entry.is_daily_rate && (
                                        <span className="inline-block mt-1 px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded">
                                          TP
                                        </span>
                                      )}
                                      {entry.description && (
                                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                          {entry.description}
                                        </p>
                                      )}
                                    </div>
                                  ))}
                                  <div className="flex justify-end pt-1">
                                    <span className="text-xs font-mono text-lime-600 dark:text-lime-400">
                                      Σ {formatHoursToHM(day.project_hours)}
                                    </span>
                                  </div>
                                </div>
                              ) : !day.is_weekend && !day.holiday && !day.absence ? (
                                <div className="p-2 rounded bg-muted/50">
                                  <p className="text-xs text-muted-foreground">Keine Aufträge</p>
                                </div>
                              ) : null}
                            </div>
                          );
                          })}
                        </div>

                        {/* Week Summary */}
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 rounded-lg bg-muted/50 border border-border">
                          <div className="flex flex-wrap items-center gap-4 md:gap-6">
                            <div>
                              <p className="text-xs text-muted-foreground uppercase">Gesamt Arbeitszeit</p>
                              <p className="text-xl font-mono font-bold text-foreground">
                                {formatHoursToHM(details.total_work_hours)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground uppercase">Gesamt Projektzeit</p>
                              <p className="text-xl font-mono font-bold text-lime-600 dark:text-lime-400">
                                {formatHoursToHM(details.total_project_hours)}
                              </p>
                            </div>
                            {details.daily_rate_count > 0 && (
                              <div>
                                <p className="text-xs text-muted-foreground uppercase">Tagespauschalen</p>
                                <p className="text-xl font-mono font-bold text-amber-600 dark:text-amber-400">
                                  {details.daily_rate_count}
                                </p>
                              </div>
                            )}
                            {/* Absence Summary */}
                            {details.absence_summary && (details.absence_summary.vacation > 0 || details.absence_summary.sick > 0 || details.absence_summary.special_leave > 0) && (
                              <div className="flex items-center gap-3 pl-4 border-l border-border">
                                {details.absence_summary.vacation > 0 && (
                                  <div className="flex items-center gap-1 text-teal-600 dark:text-teal-400" title="Urlaubstage">
                                    <Palmtree className="w-4 h-4" />
                                    <span className="text-sm font-medium">{details.absence_summary.vacation}</span>
                                  </div>
                                )}
                                {details.absence_summary.sick > 0 && (
                                  <div className="flex items-center gap-1 text-amber-600 dark:text-amber-400" title="Krankheitstage">
                                    <Thermometer className="w-4 h-4" />
                                    <span className="text-sm font-medium">{details.absence_summary.sick}</span>
                                  </div>
                                )}
                                {details.absence_summary.special_leave > 0 && (
                                  <div className="flex items-center gap-1 text-violet-600 dark:text-violet-400" title="Sonderurlaub">
                                    <Gift className="w-4 h-4" />
                                    <span className="text-sm font-medium">{details.absence_summary.special_leave}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => {
                                setFixApprovalId(approval.id);
                                setFixDialogOpen(true);
                              }}
                              variant="outline"
                              className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                              data-testid={`fix-btn-${approval.id}`}
                            >
                              <Shield className="w-4 h-4 mr-2" />
                              Verbindlich festsetzen
                            </Button>
                            <Button
                              onClick={() => handleApprove(approval.id)}
                              className="bg-emerald-500 hover:bg-emerald-600 text-white"
                            >
                              <Check className="w-4 h-4 mr-2" />
                              Woche genehmigen
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}

        {pendingApprovals.length === 0 && (
          <div className="text-center py-16">
            <CheckSquare className="w-12 h-12 text-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">Keine ausstehenden Wochen-Genehmigungen</h3>
            <p className="text-muted-foreground">Alle Wochen wurden bereits bearbeitet.</p>
          </div>
        )}
      </div>
        </TabsContent>

        {/* Overtime Requests Tab */}
        <TabsContent value="overtime" className="mt-6">
          <div className="space-y-4">
            {pendingOvertimeRequests.map((request, index) => (
              <motion.div
                key={request.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="card-glass p-4 md:p-6"
                data-testid={`overtime-request-${request.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      request.type === 'reduction' ? 'bg-blue-500/20' : 'bg-amber-500/20'
                    }`}>
                      {request.type === 'reduction' 
                        ? <ArrowDownRight className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        : <Banknote className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                      }
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-foreground">
                        {request.user_name || 'Unbekannt'}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {request.user_email}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    {/* Request details */}
                    <div className="hidden md:flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Antragstyp</p>
                        <p className={`text-sm font-medium ${request.type === 'reduction' ? 'text-blue-400' : 'text-amber-600 dark:text-amber-400'}`}>
                          {getOvertimeRequestTypeLabel(request.type)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Stunden</p>
                        <p className="text-lg font-mono font-bold text-foreground">{formatHoursToHM(Number(request.hours))}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Aktueller Saldo</p>
                        <p className={`text-lg font-mono font-bold ${request.current_balance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                          {formatHoursToHM(Number(request.current_balance || 0))}
                        </p>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleApproveOvertimeRequest(request.id)}
                        data-testid={`approve-overtime-${request.id}`}
                        className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Genehmigen
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => openOvertimeRejectDialog(request)}
                        data-testid={`reject-overtime-${request.id}`}
                        className="bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Ablehnen
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Mobile stats */}
                <div className="mt-4 md:hidden grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Typ</p>
                    <p className={`text-sm font-medium ${request.type === 'reduction' ? 'text-blue-400' : 'text-amber-600 dark:text-amber-400'}`}>
                      {request.type === 'reduction' ? 'Abbau' : 'Auszahlung'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Stunden</p>
                    <p className="text-lg font-mono font-bold text-foreground">{formatHoursToHM(Number(request.hours))}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Saldo</p>
                    <p className={`text-lg font-mono font-bold ${request.current_balance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                      {formatHoursToHM(Number(request.current_balance || 0))}
                    </p>
                  </div>
                </div>

                {/* Note if present */}
                {request.note && (
                  <div className="mt-4 p-3 rounded-lg bg-muted/50 border border-border">
                    <p className="text-sm text-muted-foreground">
                      <span className="font-medium text-muted-foreground">Notiz:</span> {request.note}
                    </p>
                  </div>
                )}

                {/* Created date */}
                <div className="mt-3 text-xs text-muted-foreground">
                  Eingereicht am {formatDateTimeBerlin(request.created_at, 'd. MMM yyyy, HH:mm')} Uhr
                </div>
              </motion.div>
            ))}

            {pendingOvertimeRequests.length === 0 && (
              <div className="text-center py-16">
                <Clock className="w-12 h-12 text-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">Keine ausstehenden Anträge zum Arbeitszeitkonto</h3>
                <p className="text-muted-foreground">Alle Anträge wurden bereits bearbeitet.</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Unlock Requests Tab */}
        <TabsContent value="unlock-requests" className="mt-6">
          <div className="space-y-4">
            {pendingUnlockRequests.map((request, index) => (
              <motion.div
                key={request.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="card-glass p-4 md:p-6"
                data-testid={`unlock-request-${request.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center">
                      <Lock className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-foreground">
                        {request.user_name}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {request.week_start 
                          ? `Woche vom ${safeFormatDate(request.week_start, 'd. MMMM yyyy')}`
                          : 'Mehrere Wochen / Zeitraum übergreifend'}
                      </p>
                    </div>
                  </div>

                  <div className="hidden md:flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground uppercase">Typ</p>
                      <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
                        {request.scope === 'day' 
                          ? `Tag ${new Date(request.date + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' })}`
                          : getCollectionLabel(request.collection_name)}
                      </p>
                    </div>
                    {request.scope !== 'day' && (
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground uppercase">Einträge</p>
                        <p className="text-lg font-mono font-bold text-foreground">
                          {request.document_ids?.length || 0}
                        </p>
                      </div>
                    )}
                    {request.scope === 'day' && (
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground uppercase">Hinweis</p>
                        <p className="text-xs text-indigo-800 dark:text-indigo-300">
                          Genehmigung gibt die Woche zur Bearbeitung zurück
                        </p>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleApproveUnlockRequest(request.id)}
                        data-testid={`approve-unlock-${request.id}`}
                        className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Genehmigen
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => openUnlockRejectDialog(request)}
                        data-testid={`reject-unlock-${request.id}`}
                        className="bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Ablehnen
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Mobile stats */}
                <div className="mt-4 md:hidden grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Typ</p>
                    <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
                      {request.scope === 'day' 
                        ? `Tag ${new Date(request.date + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' })}`
                        : getCollectionLabel(request.collection_name)}
                    </p>
                  </div>
                  {request.scope !== 'day' && (
                    <div>
                      <p className="text-xs text-muted-foreground">Einträge</p>
                      <p className="text-lg font-mono font-bold text-foreground">
                        {request.document_ids?.length || 0}
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Day unlock hint */}
                {request.scope === 'day' && (
                  <div className="mt-3 md:hidden text-xs text-indigo-800 dark:text-indigo-300">
                    Genehmigung gibt die Woche zur Bearbeitung zurück
                  </div>
                )}

                {/* Reason */}
                <div className="mt-4 p-3 rounded-lg bg-muted/50 border border-border">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium text-muted-foreground">Begründung:</span> {request.reason}
                  </p>
                </div>

                {/* Created date */}
                <div className="mt-3 text-xs text-muted-foreground">
                  Eingereicht am {formatDateTimeBerlin(request.created_at, 'd. MMM yyyy, HH:mm')} Uhr
                </div>

                {/* Mobile action buttons */}
                <div className="mt-4 md:hidden flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleApproveUnlockRequest(request.id)}
                    className="flex-1 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Genehmigen
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => openUnlockRejectDialog(request)}
                    className="flex-1 bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Ablehnen
                  </Button>
                </div>
              </motion.div>
            ))}

            {pendingUnlockRequests.length === 0 && (
              <div className="text-center py-16">
                <Unlock className="w-12 h-12 text-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">Keine ausstehenden Entsperrungsanträge</h3>
                <p className="text-muted-foreground">Alle Anträge wurden bereits bearbeitet.</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Leave Approvals Tab */}
        <TabsContent value="leaves" className="mt-6" data-testid="leaves-approval-content">
          <div className="space-y-6">
            {/* Pending Leave Requests */}
            {pendingLeaves.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-400" />
                  Ausstehende Anträge ({pendingLeaves.length})
                </h3>
                {pendingLeaves.map((leave, index) => {
                  const leaveType = leave.leave_type || leave.vacation_type;
                  const style = getLeaveTypeStyle(leaveType);
                  const LeaveIcon = getLeaveTypeIcon(leaveType);
                  const overtimeInfo = leaveType === 'overtime_reduction' ? calculateOvertimeReductionHours(leave) : null;
                  
                  // Get the full balance info including committed data
                  const balanceInfo = leaveType === 'overtime_reduction' ? (overtimeBalances[leave.user_id] ?? null) : null;
                  const finalBalance = balanceInfo?.final_balance ?? null;
                  const committedTotal = balanceInfo?.committed_total ?? 0;
                  const availableAfterCommitments = balanceInfo?.available_after_commitments ?? finalBalance;
                  
                  // The pending leave is already included in committed_total, so "frei" already accounts for this request
                  // After approval, the leave becomes reflected in final_balance instead
                  // For clarity, we show what happens after approval
                  const balanceAfterApproval = availableAfterCommitments !== null && overtimeInfo 
                    ? availableAfterCommitments  // This already includes this pending request
                    : null;
                  
                  return (
                    <motion.div
                      key={leave.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="card-glass p-4 md:p-6"
                      data-testid={`pending-leave-${leave.id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${style.bg}`}>
                            <LeaveIcon className={`w-6 h-6 ${style.text}`} />
                          </div>
                          <div>
                            <h3 className="text-lg font-medium text-foreground">
                              {leave.user?.first_name} {leave.user?.last_name}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {safeFormatDate(leave.start_date, 'd. MMM')} - {safeFormatDate(leave.end_date, 'd. MMM yyyy')}
                            </p>
                            <div className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs ${style.bg} ${style.text} border ${style.border}`}>
                              <LeaveIcon className="w-3 h-3" />
                              {getLeaveTypeLabel(leaveType)}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-2xl font-mono font-bold text-foreground">{leave.days} Tage</p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleApproveLeave(leave.id)}
                              data-testid={`approve-leave-${leave.id}`}
                              className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Genehmigen
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => openLeaveRejectDialog(leave)}
                              data-testid={`reject-leave-${leave.id}`}
                              className="bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30"
                            >
                              <X className="w-4 h-4 mr-1" />
                              Ablehnen
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Overtime Balance Info for overtime_reduction - with committed data */}
                      {leaveType === 'overtime_reduction' && overtimeInfo && balanceInfo && (
                        <div className={`mt-4 p-3 rounded-lg border ${balanceAfterApproval !== null && balanceAfterApproval < 0 ? 'bg-red-500/10 border-red-500/20' : 'bg-blue-500/10 border-blue-500/20'}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Timer className={`w-4 h-4 ${balanceAfterApproval !== null && balanceAfterApproval < 0 ? 'text-red-400' : 'text-blue-400'}`} />
                            <span className={`text-sm font-medium ${balanceAfterApproval !== null && balanceAfterApproval < 0 ? 'text-red-400' : 'text-blue-400'}`}>
                              Zeitguthaben
                            </span>
                          </div>
                          
                          {/* Main balance line with committed info */}
                          <div className="text-sm mb-3">
                            <p className="text-muted-foreground">
                              Zeitguthaben: <span className={`font-mono font-medium ${finalBalance >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatHoursToHM(finalBalance)}</span>
                              {committedTotal > 0 && (
                                <> · bereits verplant: <span className="font-mono font-medium text-amber-400">{formatHoursToHM(committedTotal)}</span></>
                              )}
                              {' '}· frei (inkl. dieses Antrags): <span className={`font-mono font-medium ${availableAfterCommitments >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatHoursToHM(availableAfterCommitments)}</span>
                            </p>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div>
                              <p className="text-muted-foreground">Dieser Antrag</p>
                              <p className="font-mono font-medium text-foreground">
                                {overtimeInfo.isEstimate ? 'ca. ' : ''}{formatHoursToHM(overtimeInfo.totalHours)}
                                <span className="text-xs text-muted-foreground ml-1">
                                  ({overtimeInfo.workDays} Werktage × {overtimeInfo.dailyHours}h)
                                </span>
                              </p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Nach Genehmigung dieses Antrags</p>
                              <p className={`font-mono font-medium ${balanceAfterApproval >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                {overtimeInfo.isEstimate ? 'ca. ' : ''}{formatHoursToHM(balanceAfterApproval)}
                              </p>
                            </div>
                          </div>
                          
                          {balanceAfterApproval !== null && balanceAfterApproval < 0 && (
                            <div className="mt-2 flex items-center gap-2 text-red-400 text-xs">
                              <AlertCircle className="w-3.5 h-3.5" />
                              <span>Guthaben reicht nicht aus – es entstehen Minusstunden.</span>
                            </div>
                          )}
                        </div>
                      )}

                      {leave.notes && (
                        <div className="mt-3 p-3 rounded-lg bg-muted/50 border border-border">
                          <p className="text-sm text-muted-foreground">{leave.notes}</p>
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}

            {/* Pending Cancellation Requests */}
            {cancelPendingLeaves.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground flex items-center gap-2">
                  <RotateCcw className="w-5 h-5 text-amber-400" />
                  Ausstehende Stornierungen ({cancelPendingLeaves.length})
                </h3>
                {cancelPendingLeaves.map((leave, index) => {
                  const leaveType = leave.leave_type || leave.vacation_type;
                  const style = getLeaveTypeStyle(leaveType);
                  const LeaveIcon = getLeaveTypeIcon(leaveType);
                  
                  return (
                    <motion.div
                      key={leave.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="card-glass p-4 md:p-6 border-l-4 border-l-amber-500"
                      data-testid={`cancel-pending-${leave.id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center">
                            <RotateCcw className="w-6 h-6 text-amber-400" />
                          </div>
                          <div>
                            <h3 className="text-lg font-medium text-foreground">
                              {leave.user?.first_name} {leave.user?.last_name}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {safeFormatDate(leave.start_date, 'd. MMM')} - {safeFormatDate(leave.end_date, 'd. MMM yyyy')}
                            </p>
                            <div className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs ${style.bg} ${style.text} border ${style.border}`}>
                              <LeaveIcon className="w-3 h-3" />
                              {getLeaveTypeLabel(leaveType)} - {leave.days} Tage
                            </div>
                          </div>
                        </div>

                        {leave.can_withdraw ? (
                          <div className="flex flex-col items-start md:items-end gap-1">
                            <Button
                              size="sm"
                              onClick={() => handleWithdrawCancellation(leave.id)}
                              data-testid={`withdraw-cancel-${leave.id}`}
                              className="bg-muted text-foreground border border-border hover:bg-accent"
                            >
                              <RotateCcw className="w-4 h-4 mr-1" />
                              Zurückziehen
                            </Button>
                            <span className="text-xs text-muted-foreground">Ihr Vorschlag – wartet auf die Person</span>
                          </div>
                        ) : (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleConfirmCancellation(leave.id)}
                            data-testid={`confirm-cancel-${leave.id}`}
                            className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30"
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Bestätigen
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => openCancelRejectDialog(leave)}
                            data-testid={`reject-cancel-${leave.id}`}
                            className="bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Ablehnen
                          </Button>
                        </div>
                        )}                        
                      </div>

                      {leave.cancellation_reason && (
                        <div className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                          <p className="text-sm text-amber-400">
                            <Ban className="w-3 h-3 inline mr-1" />
                            Grund: {leave.cancellation_reason}
                          </p>
                        </div>
                      )}

                      {leave.cancellation_requester && (
                        <p className="mt-2 text-xs text-muted-foreground">
                          Angefragt von: {leave.cancellation_requester.first_name} {leave.cancellation_requester.last_name}
                        </p>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}

            {/* Empty state */}
            {pendingLeaves.length === 0 && cancelPendingLeaves.length === 0 && (
              <div className="text-center py-16">
                <Palmtree className="w-12 h-12 text-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">Keine ausstehenden Abwesenheitsanträge</h3>
                <p className="text-muted-foreground">Alle Anträge wurden bereits bearbeitet.</p>
              </div>
            )}
            {/* Genehmigte Abwesenheiten im Team – Stornierung vorschlagen */}
            <TeamLeavesPanel onChanged={fetchPendingLeaves} />            
          </div>
        </TabsContent>

        {/* Average Working Time Tab */}
        <TabsContent value="average-worktime" className="mt-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-glass p-6"
          >
            <h2 className="text-lg font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              Ø Arbeitszeit-Übersicht (§ 3 Satz 2 ArbZG)
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              Mitarbeiter, deren werktägliche Durchschnittsarbeitszeit über dem Ausgleichszeitraum die 8h-Grenze überschreitet oder sich ihr nähert.
            </p>
            <TeamAverageViolationsList />
          </motion.div>
        </TabsContent>

        {/* Sunday Compliance Tab */}
        <TabsContent value="sunday-compliance" className="mt-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Rules Info Box */}
            <SundayRulesInfoBox compact={false} />
            
            {/* Free Sundays Counter */}
            <div className="card-glass p-6">
              <h3 className="text-lg font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                Freie Sonntage im Jahr
              </h3>
              <FreeSundaysCounter year={new Date().getFullYear()} />
            </div>
            
            {/* Team Compliance */}
            <div className="card-glass p-6">
              <h3 className="text-lg font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                <Sun className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                Ersatzruhetag-Status (letzte 90 Tage)
              </h3>
              <SundayComplianceTeamList 
                dateFrom={format(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd')}
                dateTo={format(new Date(), 'yyyy-MM-dd')}
              />
            </div>
          </motion.div>
        </TabsContent>

        {/* Escalations Tab (Admin only) */}
        {user?.role === 'admin' && (
          <TabsContent value="escalations" className="mt-6">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="card-glass p-6">
                <EscalationList />
              </div>
            </motion.div>
          </TabsContent>
        )}

        {/* Deputy Tab (Supervisor/Admin) */}
        {(user?.role === 'supervisor' || user?.role === 'admin') && (
          <TabsContent value="deputy" className="mt-6">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="card-glass p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-heading font-semibold text-foreground">
                      Vertreterregelung
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Verwalten Sie Ihre Vertretung während Ihrer Abwesenheit
                    </p>
                  </div>
                </div>
                
                <DeputyActivationPanel />
              </div>
            </motion.div>
          </TabsContent>
        )}
      </Tabs>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-heading">
              {selectedDay 
                ? `Tag ablehnen: ${safeFormatDate(selectedDay.date, 'd. MMMM yyyy')}`
                : 'Woche ablehnen'
              }
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {selectedDay && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 mb-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm font-medium">Hinweis</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Die gesamte Woche wird zur Korrektur zurückgegeben, mit einem Hinweis auf den abgelehnten Tag.
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="text-muted-foreground">Grund für die Ablehnung</Label>
              <Textarea
                data-testid="reject-reason-input"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="bg-input border-border text-foreground resize-none"
                placeholder={selectedDay 
                  ? "Was stimmt an diesem Tag nicht?" 
                  : "Grund für die Ablehnung der Woche..."
                }
                rows={4}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setRejectDialogOpen(false)}
              className="text-muted-foreground hover:text-foreground"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleReject}
              data-testid="confirm-reject-button"
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {selectedDay ? 'Tag ablehnen' : 'Woche ablehnen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Overtime Reject Dialog */}
      <Dialog open={overtimeRejectDialogOpen} onOpenChange={setOvertimeRejectDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-heading">
              Antrag zum Arbeitszeitkonto ablehnen
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {selectedOvertimeRequest && (
              <div className="p-3 rounded-lg bg-muted/50 border border-border">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Mitarbeiter</p>
                    <p className="text-foreground font-medium">{selectedOvertimeRequest.user_name}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Antragstyp</p>
                    <p className={`font-medium ${selectedOvertimeRequest.type === 'reduction' ? 'text-blue-400' : 'text-amber-600 dark:text-amber-400'}`}>
                      {getOvertimeRequestTypeLabel(selectedOvertimeRequest.type)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Beantragte Stunden</p>
                    <p className="text-foreground font-mono font-medium">{formatHoursToHM(Number(selectedOvertimeRequest.hours))}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Aktueller Saldo</p>
                    <p className={`font-mono font-medium ${selectedOvertimeRequest.current_balance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                      {formatHoursToHM(Number(selectedOvertimeRequest.current_balance || 0))}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="text-muted-foreground">Grund für die Ablehnung (optional)</Label>
              <Textarea
                data-testid="overtime-reject-reason-input"
                value={overtimeRejectReason}
                onChange={(e) => setOvertimeRejectReason(e.target.value)}
                className="bg-input border-border text-foreground resize-none"
                placeholder="z.B. Nicht genügend Zeitguthaben verfügbar..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOvertimeRejectDialogOpen(false)}
              className="text-muted-foreground hover:text-foreground"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleRejectOvertimeRequest}
              data-testid="confirm-overtime-reject-button"
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Antrag ablehnen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Entry Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-heading">
              Arbeitszeit korrigieren
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {editEntry && (
              <div className="p-3 rounded-lg bg-muted/50 border border-border">
                <p className="text-sm text-muted-foreground">
                  Aktuell: <span className="text-foreground font-mono">{editEntry.start_time} - {editEntry.end_time}</span>
                  {' '}({formatHoursToHM(Number(editEntry.hours))}, Pause: {editEntry.break_minutes} Min)
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Beginn</Label>
                <Input
                  type="time"
                  data-testid="edit-start-time"
                  value={editForm.start_time}
                  onChange={(e) => setEditForm(prev => ({ ...prev, start_time: e.target.value }))}
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Ende</Label>
                <Input
                  type="time"
                  data-testid="edit-end-time"
                  value={editForm.end_time}
                  onChange={(e) => setEditForm(prev => ({ ...prev, end_time: e.target.value }))}
                  className="bg-input border-border text-foreground"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Pause (Minuten)</Label>
              <Input
                type="number"
                data-testid="edit-break-minutes"
                value={editForm.break_minutes}
                onChange={(e) => setEditForm(prev => ({ ...prev, break_minutes: parseInt(e.target.value) || 0 }))}
                className="bg-input border-border text-foreground"
              />
            </div>

            <div className="p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Neue Arbeitszeit:</span>
                <span className="text-xl font-mono font-bold text-indigo-600 dark:text-indigo-400">
                  {formatHoursToHM(calculateEditHours(editForm.start_time || '08:00', editForm.end_time || '17:00', editForm.break_minutes))}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">
                Begründung <span className="text-red-600 dark:text-red-400">*</span>
              </Label>
              <Textarea
                data-testid="edit-comment-input"
                value={editForm.comment}
                onChange={(e) => setEditForm(prev => ({ ...prev, comment: e.target.value }))}
                className="bg-input border-border text-foreground resize-none"
                placeholder="Warum wird die Arbeitszeit korrigiert?"
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                Der Mitarbeiter wird über die Änderung und Ihren Kommentar informiert.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setEditDialogOpen(false)}
              className="text-muted-foreground hover:text-foreground"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleEditEntry}
              data-testid="confirm-edit-button"
              disabled={!editForm.comment.trim()}
              className="bg-indigo-500 hover:bg-indigo-600 text-white disabled:opacity-50"
            >
              Korrigieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GoBD Delete Confirmation Dialog */}
      <GoBDDeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={deleteConfig.title}
        itemDescription={deleteConfig.itemDescription}
        requireReason={deleteConfig.requireReason}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {/* Audit History Panel Dialog */}
      <Dialog open={historyPanelOpen} onOpenChange={setHistoryPanelOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <History className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              Änderungshistorie
            </DialogTitle>
          </DialogHeader>
          {historyEntry.id && (
            <AuditHistoryPanel
              collectionName={historyEntry.collection}
              documentId={historyEntry.id}
              isOpen={true}
              compact={false}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Unlock Request Reject Dialog */}
      <Dialog open={unlockRejectDialogOpen} onOpenChange={setUnlockRejectDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-heading">
              Entsperrungsantrag ablehnen
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {selectedUnlockRequest && (
              <div className="p-3 rounded-lg bg-muted/50 border border-border">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Mitarbeiter</p>
                    <p className="text-foreground font-medium">{selectedUnlockRequest.user_name}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Typ</p>
                    <p className="text-indigo-600 dark:text-indigo-400 font-medium">
                      {getCollectionLabel(selectedUnlockRequest.collection_name)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Einträge</p>
                    <p className="text-foreground font-mono font-medium">
                      {selectedUnlockRequest.document_ids?.length || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Woche</p>
                    <p className="text-foreground font-medium">
                      {selectedUnlockRequest.week_start 
                        ? safeFormatDate(selectedUnlockRequest.week_start, 'd. MMM')
                        : 'Mehrere Wochen'}
                    </p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-muted-foreground text-xs">Begründung des Mitarbeiters:</p>
                  <p className="text-sm text-muted-foreground mt-1">{selectedUnlockRequest.reason}</p>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="text-muted-foreground">Grund für die Ablehnung (Pflichtfeld)</Label>
              <Textarea
                data-testid="unlock-reject-reason-input"
                value={unlockRejectReason}
                onChange={(e) => setUnlockRejectReason(e.target.value)}
                className="bg-input border-border text-foreground resize-none"
                placeholder="Warum wird dieser Antrag abgelehnt?"
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                Der Mitarbeiter sieht diese Begründung in seinen Entsperrungsanträgen.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setUnlockRejectDialogOpen(false)}
              className="text-muted-foreground hover:text-foreground"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleRejectUnlockRequest}
              data-testid="confirm-unlock-reject-button"
              disabled={!unlockRejectReason.trim()}
              className="bg-red-500 hover:bg-red-600 text-white disabled:opacity-50"
            >
              Antrag ablehnen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Leave Reject Dialog */}
      <Dialog open={leaveRejectDialogOpen} onOpenChange={setLeaveRejectDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-heading">
              Abwesenheitsantrag ablehnen
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {selectedLeaveRequest && (
              <div className="p-3 rounded-lg bg-muted/50 border border-border">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Mitarbeiter</p>
                    <p className="text-foreground font-medium">
                      {selectedLeaveRequest.user?.first_name} {selectedLeaveRequest.user?.last_name}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Typ</p>
                    <p className="text-foreground font-medium">
                      {getLeaveTypeLabel(selectedLeaveRequest.leave_type || selectedLeaveRequest.vacation_type)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Zeitraum</p>
                    <p className="text-foreground font-medium">
                      {safeFormatDate(selectedLeaveRequest.start_date, 'd. MMM')} - {safeFormatDate(selectedLeaveRequest.end_date, 'd. MMM yyyy')}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Tage</p>
                    <p className="text-foreground font-mono font-medium">
                      {selectedLeaveRequest.days}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="text-muted-foreground">Grund für die Ablehnung</Label>
              <Textarea
                data-testid="leave-reject-reason-input"
                value={leaveRejectReason}
                onChange={(e) => setLeaveRejectReason(e.target.value)}
                className="bg-input border-border text-foreground resize-none"
                placeholder="Warum wird dieser Antrag abgelehnt?"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setLeaveRejectDialogOpen(false)}
              className="text-muted-foreground hover:text-foreground"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleRejectLeave}
              data-testid="confirm-leave-reject-button"
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Antrag ablehnen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancellation Reject Dialog */}
      <Dialog open={cancelRejectDialogOpen} onOpenChange={setCancelRejectDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-heading">
              Stornierung ablehnen
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {selectedCancelRequest && (
              <div className="p-3 rounded-lg bg-muted/50 border border-border">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Mitarbeiter</p>
                    <p className="text-foreground font-medium">
                      {selectedCancelRequest.user?.first_name} {selectedCancelRequest.user?.last_name}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Typ</p>
                    <p className="text-foreground font-medium">
                      {getLeaveTypeLabel(selectedCancelRequest.leave_type || selectedCancelRequest.vacation_type)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Zeitraum</p>
                    <p className="text-foreground font-medium">
                      {safeFormatDate(selectedCancelRequest.start_date, 'd. MMM')} - {safeFormatDate(selectedCancelRequest.end_date, 'd. MMM yyyy')}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Tage</p>
                    <p className="text-foreground font-mono font-medium">
                      {selectedCancelRequest.days}
                    </p>
                  </div>
                </div>
                {selectedCancelRequest.cancellation_reason && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <p className="text-muted-foreground text-xs">Stornierungsgrund:</p>
                    <p className="text-sm text-muted-foreground mt-1">{selectedCancelRequest.cancellation_reason}</p>
                  </div>
                )}
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="text-muted-foreground">Grund für die Ablehnung (optional)</Label>
              <Textarea
                data-testid="cancel-reject-reason-input"
                value={cancelRejectReason}
                onChange={(e) => setCancelRejectReason(e.target.value)}
                className="bg-input border-border text-foreground resize-none"
                placeholder="Warum wird die Stornierung abgelehnt?"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setCancelRejectDialogOpen(false)}
              className="text-muted-foreground hover:text-foreground"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleRejectCancellation}
              data-testid="confirm-cancel-reject-button"
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Stornierung ablehnen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fix Approval Dialog */}
      <FixApprovalDialog
        open={fixDialogOpen}
        onOpenChange={setFixDialogOpen}
        approvalId={fixApprovalId}
        onSuccess={() => {
          fetchApprovals();
          setFixApprovalId(null);
        }}
      />
    </div>
  );
}
